import React, { useState } from 'react';
import { StaticPageLayout } from '@/components/StaticPageLayout';
import { Mail, Linkedin, Globe, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type BoardMember = {
  id: number;
  name: string;
  role: string;
  bio: string;
  image: string;
  email?: string;
  linkedin?: string;
  website?: string;
  category: string;
};

type Props = {
  board_members: BoardMember[];
};

export default function BoardOfDirectors({ board_members }: Props) {
  const [selectedMember, setSelectedMember] = useState<BoardMember | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <StaticPageLayout>
      {/* Hero Section */}
      <section className="bg-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="py-12 md:py-20">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-sm uppercase tracking-widest text-primary mb-4">
                  GOVERNANCE
                </p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6">
                  Board of
                  <span className="italic"> Directors</span>
                  <br />
                  <span className="font-semibold">guiding our vision</span>
                </h1>
                <p className="text-xl text-gray-700 leading-relaxed max-w-2xl">
                  Our Board of Directors brings together distinguished
                  professionals in science, medicine, and education, all
                  committed to supporting diversity and mentorship in higher
                  education.
                </p>
              </div>

              {/* Creative Visual - Constellation of Leadership */}
              <div className="relative h-[400px] hidden lg:block">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 400 400"
                  className="absolute inset-0"
                >
                  {/* Central governance node */}
                  <g>
                    <circle
                      cx="200"
                      cy="200"
                      r="60"
                      fill="none"
                      stroke="rgb(147 51 234)"
                      strokeWidth="1"
                      opacity="0.2"
                      strokeDasharray="4,4"
                    />
                    <circle
                      cx="200"
                      cy="200"
                      r="40"
                      fill="none"
                      stroke="rgb(147 51 234)"
                      strokeWidth="1"
                      opacity="0.3"
                    />
                    <circle
                      cx="200"
                      cy="200"
                      r="20"
                      fill="white"
                      stroke="rgb(147 51 234)"
                      strokeWidth="2"
                      opacity="0.8"
                    />
                    <circle
                      cx="200"
                      cy="200"
                      r="8"
                      fill="rgb(147 51 234)"
                      opacity="0.4"
                    />
                  </g>

                  {/* Orbiting director nodes */}
                  {[...Array(5)].map((_, i) => {
                    const angle = (i * 72 * Math.PI) / 180;
                    const radius = 140;
                    const x = 200 + Math.cos(angle - Math.PI / 2) * radius;
                    const y = 200 + Math.sin(angle - Math.PI / 2) * radius;

                    return (
                      <g key={i}>
                        {/* Connection lines */}
                        <line
                          x1="200"
                          y1="200"
                          x2={x}
                          y2={y}
                          stroke="rgb(147 51 234)"
                          strokeWidth="1"
                          opacity="0.1"
                          strokeDasharray="2,4"
                          className="animate-pulse"
                          style={{ animationDelay: `${i * 0.3}s` }}
                        />

                        {/* Node glow */}
                        <circle
                          cx={x}
                          cy={y}
                          r="25"
                          fill="rgb(147 51 234)"
                          opacity="0.05"
                          className="animate-pulse"
                          style={{ animationDelay: `${i * 0.3}s` }}
                        />

                        {/* Main node */}
                        <circle
                          cx={x}
                          cy={y}
                          r="15"
                          fill="white"
                          stroke="rgb(147 51 234)"
                          strokeWidth="2"
                          opacity="0.9"
                        />

                        {/* Inner dot */}
                        <circle
                          cx={x}
                          cy={y}
                          r="5"
                          fill="rgb(147 51 234)"
                          opacity="0.5"
                        />

                        {/* Expertise connections */}
                        {i < 4 && (
                          <line
                            x1={x}
                            y1={y}
                            x2={
                              200 +
                              Math.cos(
                                ((i + 1) * 72 * Math.PI) / 180 - Math.PI / 2
                              ) *
                                radius
                            }
                            y2={
                              200 +
                              Math.sin(
                                ((i + 1) * 72 * Math.PI) / 180 - Math.PI / 2
                              ) *
                                radius
                            }
                            stroke="rgb(147 51 234)"
                            strokeWidth="0.5"
                            opacity="0.05"
                          />
                        )}
                      </g>
                    );
                  })}

                  {/* Floating expertise particles */}
                  {[...Array(15)].map((_, i) => (
                    <circle
                      key={`particle-${i}`}
                      cx={100 + Math.random() * 200}
                      cy={100 + Math.random() * 200}
                      r="1.5"
                      fill="rgb(147 51 234)"
                      opacity={0.2 + Math.random() * 0.3}
                      className="animate-twinkle"
                      style={{
                        animationDelay: `${Math.random() * 5}s`,
                        animationDuration: `${3 + Math.random() * 4}s`,
                      }}
                    />
                  ))}

                  {/* Rotating rings */}
                  <g
                    className="animate-rotate-slow"
                    style={{ transformOrigin: '200px 200px' }}
                  >
                    <circle
                      cx="200"
                      cy="200"
                      r="100"
                      fill="none"
                      stroke="rgb(147 51 234)"
                      strokeWidth="0.5"
                      opacity="0.1"
                      strokeDasharray="10,20"
                    />
                  </g>
                  <g
                    className="animate-rotate-reverse"
                    style={{ transformOrigin: '200px 200px' }}
                  >
                    <circle
                      cx="200"
                      cy="200"
                      r="120"
                      fill="none"
                      stroke="rgb(147 51 234)"
                      strokeWidth="0.5"
                      opacity="0.05"
                      strokeDasharray="5,15"
                    />
                  </g>
                </svg>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
                  <p className="text-xs text-gray-400">
                    Leadership through collective wisdom
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Board Members Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="text-sm uppercase tracking-widest text-primary mb-4">
              MEET OUR DIRECTORS
            </h2>
            <p className="text-3xl font-light text-gray-900">
              Experts dedicated to educational equity
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {board_members.map((member) => (
              <div
                key={member.id}
                className="group relative cursor-pointer"
                onClick={() => {
                  setSelectedMember(member);
                  setIsModalOpen(true);
                }}
              >
                {/* Card content */}
                <div className="relative bg-white p-6 border border-gray-200 hover:border-primary/30 transition-all duration-300 rounded-lg hover:shadow-lg">
                  {/* Profile picture */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200 group-hover:border-primary/30 transition-colors flex-shrink-0 bg-gray-50">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-1">
                        {member.name}
                      </h3>
                      <p className="text-xs text-primary uppercase tracking-wider">
                        {member.role}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                    {member.bio}
                  </p>

                  {/* See more link */}
                  <button className="text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1">
                    View full profile
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {/* Hover effect line */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/20 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-b-lg"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Statement */}
      {/* <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
            Governance with purpose
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Our Board of Directors provides strategic oversight and guidance,
            ensuring Project SHORT remains true to its mission of democratizing
            access to higher education through mentorship.
          </p>
        </div>
      </section> */}

      {/* Member Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl">
          {selectedMember && (
            <>
              <DialogHeader className="sr-only">
                <DialogTitle>
                  {selectedMember.name} - {selectedMember.role}
                </DialogTitle>
              </DialogHeader>

              <div className="p-2">
                {/* Header section with image and basic info */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-8">
                  <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-gray-200 flex-shrink-0 bg-gray-50">
                    <img
                      src={selectedMember.image}
                      alt={selectedMember.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="text-center sm:text-left flex-1">
                    <h3 className="text-2xl font-light text-gray-900 mb-1">
                      {selectedMember.name}
                    </h3>
                    <p className="text-base text-primary mb-4">
                      {selectedMember.role}
                    </p>
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/5 rounded-full">
                      <span className="text-sm text-primary">
                        Board of Directors
                      </span>
                    </div>
                  </div>

                  {/* Contact links - moved to top right */}
                  {(selectedMember.email ||
                    selectedMember.linkedin ||
                    selectedMember.website) && (
                    <div className="flex gap-3">
                      {selectedMember.email && (
                        <a
                          href={`mailto:${selectedMember.email}`}
                          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-primary/10 hover:text-primary transition-all"
                          aria-label="Email"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                      )}
                      {selectedMember.linkedin && (
                        <a
                          href={selectedMember.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-primary/10 hover:text-primary transition-all"
                          aria-label="LinkedIn"
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {selectedMember.website && (
                        <a
                          href={selectedMember.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-primary/10 hover:text-primary transition-all"
                          aria-label="Website"
                        >
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* Bio section */}
                <div className="space-y-6">
                  {selectedMember.bio && (
                    <div>
                      <h4 className="text-sm font-medium uppercase tracking-wider text-gray-500 mb-3">
                        About
                      </h4>
                      <p className="text-gray-700 leading-relaxed text-base">
                        {selectedMember.bio}
                      </p>
                    </div>
                  )}

                  {/* Additional info section */}
                  {/* <div className="pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-medium uppercase tracking-wider text-gray-500 mb-3">
                      Board Leadership
                    </h4>
                    <p className="text-gray-700 leading-relaxed text-base">
                      As a member of Project SHORT's Board of Directors,{' '}
                      {selectedMember.name.split(' ')[0]} provides strategic
                      oversight and governance, helping guide our organization's
                      mission to expand access to higher education. Their
                      expertise and commitment ensure we maintain the highest
                      standards of integrity and impact.
                    </p>
                  </div> */}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </StaticPageLayout>
  );
}
